import type { TaxSpec } from './item';
import type { Scheduled } from './scheduled';
import { monthsBetween, type MonthIndex, type YearMonth } from './time';

/*
 * Tax presets: data, never engine branches (docs/PLAN.md §3.2). Applying a preset COPIES its
 * values into the item, so users can edit everything and projects don't change when presets are
 * updated. Every preset records when it was checked and the official sources it follows.
 * Review yearly against the sources; see docs/tax/bulgaria.md for the worked examples.
 */

export interface TaxPresetSource {
  title: string;
  url: string;
}

export interface TaxPresetContext {
  start: YearMonth;
  horizonMonths: number;
  /** Localised name for the social-contributions deduction. */
  contributionsName: string;
}

export interface TaxPreset {
  id: string;
  /** Date the figures were last checked against the sources (ISO). */
  asOf: string;
  sources: readonly TaxPresetSource[];
  /** Everything except `sources` (what is taxed is chosen by the user). */
  build: (ctx: TaxPresetContext) => Omit<TaxSpec, 'sources'>;
}

/**
 * A value that changes on a calendar month, placed on the project's month axis: a constant when
 * the change falls outside the horizon, otherwise a two-period schedule.
 */
export function calendarStep<T>(
  start: YearMonth,
  horizonMonths: number,
  before: T,
  changeAt: YearMonth,
  after: T,
): Scheduled<T> {
  const at = monthsBetween(start, changeAt);
  if (at <= 0) return { kind: 'constant', value: after };
  if (at >= horizonMonths) return { kind: 'constant', value: before };
  return {
    kind: 'schedule',
    segments: [
      { from: 0 as MonthIndex, value: before },
      { from: at as MonthIndex, value: after },
    ],
  };
}

const NRA_INCOME_TAX: TaxPresetSource = {
  title: 'NRA — Advance tax on employment income (ZDDFL Art. 42, 48, 49)',
  url: 'https://nra.bg/wps/portal/nra/taxes/godishen-danak-varhu-dohdite/avansov-danak-varhu-dohodite',
};
const NRA_INSURABLE_INCOME: TaxPresetSource = {
  title: 'NRA — Insurable income 2026 (maximum €2,111.64 until 31.07.2026; €2,300 from 01.08.2026)',
  url: 'https://nra.bg/wps/portal/nra/osiguryavane/osiguritelen-dohod-2',
};
const NRA_HEALTH: TaxPresetSource = {
  title: 'NRA — Health insurance contributions 2026 (8 %, employer:employee 60:40)',
  url: 'https://nra.bg/wps/portal/nra/osiguryavane/zdravno-osiguryavane/razmer-osiguritelni-vnoski',
};
const NSSI_2026: TaxPresetSource = {
  title: 'NSSI — Insurable income and contributions from 1 August 2026',
  url: 'https://www.noi.bg/dohod01082026/',
};
const SSB_LAW_2026: TaxPresetSource = {
  title: 'State Social Security Budget Act 2026, State Gazette No. 68/28.07.2026',
  url: 'https://dv.parliament.bg/DVWeb/showMaterialDV.jsp?idMat=244982',
};
const NRA_INTEREST: TaxPresetSource = {
  title:
    'NRA — Interest income (final tax 10 %; EU/EEA bank deposit interest exempt, ZDDFL Art. 13(1)(7))',
  url: 'https://nra.bg/wps/portal/nra/taxes/okonchatelen-danak-varhu-dohodi/dohodi-ot-lihvi',
};

/**
 * Employee contributions for a person born after 31 Dec 1959 on a standard employment contract
 * (3rd labour category), 2026: Pensions 6.58 % + General illness & maternity 1.4 % + Unemployment
 * 0.4 % + Supplementary pension (UPF) 2.2 % + Health 3.2 % = 13.78 % of gross pay, capped at the
 * maximum insurable income. (The reduced 1.8.–31.12.2026 split in SSB Act 2026 applies only to
 * state officials and the judiciary, not to ordinary employees.)
 */
export const BG_EMPLOYEE_CONTRIBUTION_RATE = 0.1378;
export const BG_INCOME_TAX_RATE = 0.1;
export const BG_MAX_INSURABLE_INCOME_JAN_JUL_2026 = 2111.64;
export const BG_MAX_INSURABLE_INCOME_FROM_AUG_2026 = 2300;

export const TAX_PRESETS: Record<'bg-employment-2026' | 'bg-interest-2026', TaxPreset> = {
  'bg-employment-2026': {
    id: 'bg-employment-2026',
    asOf: '2026-09-27',
    sources: [NRA_INCOME_TAX, NRA_INSURABLE_INCOME, NRA_HEALTH, NSSI_2026, SSB_LAW_2026],
    build: ({ start, horizonMonths, contributionsName }) => ({
      kind: 'tax',
      deductions: [
        {
          id: 'social',
          name: contributionsName,
          rate: { kind: 'constant', value: BG_EMPLOYEE_CONTRIBUTION_RATE },
          cap: calendarStep(
            start,
            horizonMonths,
            BG_MAX_INSURABLE_INCOME_JAN_JUL_2026,
            '2026-08' as YearMonth,
            BG_MAX_INSURABLE_INCOME_FROM_AUG_2026,
          ),
          emitAsFlow: true,
        },
      ],
      brackets: { kind: 'constant', value: [{ from: 0, rate: BG_INCOME_TAX_RATE }] },
      indexThresholds: 'none',
      settlement: 'monthly',
      presetId: 'bg-employment-2026',
    }),
  },
  'bg-interest-2026': {
    id: 'bg-interest-2026',
    asOf: '2026-09-27',
    sources: [NRA_INTEREST],
    build: () => ({
      kind: 'tax',
      deductions: [],
      brackets: { kind: 'constant', value: [{ from: 0, rate: 0.1 }] },
      indexThresholds: 'none',
      settlement: 'monthly',
      presetId: 'bg-interest-2026',
    }),
  },
};

export type TaxPresetId = keyof typeof TAX_PRESETS;
